import React, { useEffect, useMemo, useState } from 'react';
import { Button, Input, message } from 'antd';
import { useAppDispatch } from '../../hooks/redux';
import { addComment, fetchCommentsByProduct } from '../../services';
import { CommentComposerProps } from '../interfaces';

const { TextArea } = Input;
const MAX_COMMENT_LENGTH = 1000;
const MAX_COMMENT_IMAGES = 4;
const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

const CommentComposer = ({
    userId,
    productId,
    parentId,
    buttonLabel,
    placeholder,
    onSubmitted,
}: CommentComposerProps) => {
    const dispatch = useAppDispatch();
    const [content, setContent] = useState('');
    const [images, setImages] = useState<File[]>([]);
    const [loading, setLoading] = useState(false);

    const previews = useMemo(
        () => images.map((file) => ({ file, url: URL.createObjectURL(file) })),
        [images]
    );

    useEffect(() => {
        return () => previews.forEach((preview) => URL.revokeObjectURL(preview.url));
    }, [previews]);

    const handleImageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const selectedFiles = Array.from(event.target.files || []);
        const validFiles = selectedFiles.filter((file) => ALLOWED_IMAGE_TYPES.includes(file.type));

        if (validFiles.length !== selectedFiles.length) {
            message.error('Images must be JPEG, PNG, or WebP.');
        }

        setImages((current) => [...current, ...validFiles].slice(0, MAX_COMMENT_IMAGES));
        event.target.value = '';
    };

    const handleSubmit = async () => {
        const trimmedContent = content.trim();

        if (!trimmedContent || !userId || !productId) {
            message.error('Content cannot be empty and user must be logged in.');
            return;
        }

        setLoading(true);
        try {
            await dispatch(addComment({
                userId,
                productId,
                content: trimmedContent,
                parentId,
                images,
            })).unwrap();
            setContent('');
            setImages([]);
            onSubmitted?.();
            message.success(parentId ? 'Reply added.' : 'Comment added.');
            if (images.length > 0) {
                dispatch(fetchCommentsByProduct({ productId }));
            }
        } catch (error) {
            message.error(error instanceof Error ? error.message : 'Failed to add comment.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="comment-composer">
            <TextArea
                value={content}
                onChange={(event) => setContent(event.target.value)}
                rows={parentId ? 2 : 3}
                placeholder={placeholder}
                maxLength={MAX_COMMENT_LENGTH}
            />
            {previews.length > 0 && (
                <div className="comment-image-preview-row">
                    {previews.map((preview) => (
                        <div className="comment-image-preview" key={`${preview.file.name}-${preview.url}`}>
                            <img src={preview.url} alt={preview.file.name} />
                            <button
                                type="button"
                                onClick={() => setImages((current) => current.filter((image) => image !== preview.file))}
                            >
                                Remove
                            </button>
                        </div>
                    ))}
                </div>
            )}
            <div className="button-block">
                <label className="comment-upload-button">
                    Add photo
                    <input
                        type="file"
                        accept="image/jpeg,image/png,image/webp"
                        multiple
                        onChange={handleImageChange}
                    />
                </label>
                <Button
                    type="primary"
                    onClick={handleSubmit}
                    loading={loading}
                    disabled={!content.trim() || loading}
                >
                    {buttonLabel}
                </Button>
                <div className="char-count">{MAX_COMMENT_LENGTH - content.length}</div>
            </div>
        </div>
    );
};

export default CommentComposer;
